import type {Optional} from '@yandex-delivery/types'
import {
  applySpec,
  castArray,
  constant,
  defaultTo,
  equals,
  filter,
  flow,
  get,
  groupBy,
  map,
  merge,
  pipe,
  sortBy,
  stubArray,
  stubString,
  toUndefined,
} from '@yandex-delivery/utils'

import type {
  DestinationPoint,
  DestinationPointDeliveryMethod,
  SddClaimProduct,
  SddClaimProducts,
  SddClaimRoutePoint,
  SearchedSddClaim,
  SearchedSddClaimRoutePoint,
  SearchedSddClaimRoutePoints,
} from '../types'
import {generateUniqueId} from './generate-unique-id'
import {toFilledParcels, toInitialParcels} from './to-parcel'
import {toPointAddress} from './to-point-address'
import {toPointContact} from './to-point-contact'

// --- Type Helpers ---
const toDestinationPointType: () => 'destination' = constant('destination')
const toDestinationPointDeliveryMethod: () => 'address' = constant('address')

// --- Route Point Getters ---
const getRoutePointId: (
  routePoint?: SearchedSddClaimRoutePoint,
) => Optional<number> = get('id')
const getRoutePointExternalOrderId: (
  routePoint: SddClaimRoutePoint,
) => Optional<string> = get('external_order_id')
const getRoutePoints: (
  sddClaim?: SearchedSddClaim,
) => Optional<SearchedSddClaimRoutePoints> = get('route_points')
const getVisitOrder: (routePoint: SearchedSddClaimRoutePoint) => number =
  get('visit_order')

// --- Route Point Filters & Sorters ---
const isDestinationPoint: (routePoints: SearchedSddClaimRoutePoint) => boolean =
  flow(get('type'), equals('destination'))
const filterDestinationPoints: (
  routePoints?: SearchedSddClaimRoutePoints,
) => SearchedSddClaimRoutePoints = filter(isDestinationPoint)
const sortByVisitOrder: (
  routePoints: SearchedSddClaimRoutePoints,
) => SearchedSddClaimRoutePoints = sortBy(getVisitOrder)

// --- Route Point Transformers ---
const basePointSpec = {
  _type: toDestinationPointType,
  _deliveryMethod: toDestinationPointDeliveryMethod,
  pickUpPoint: toUndefined,
  address: toPointAddress,
  contact: toPointContact,
  paymentOnDelivery: flow(get('payment_on_delivery'), Boolean),
}

const toInitialDestinationPoint: (
  routePoint?: SearchedSddClaimRoutePoint,
) => DestinationPoint = applySpec<DestinationPoint>({
  ...basePointSpec,
  externalID: stubString,
  parcels: toInitialParcels,
  _id: generateUniqueId,
})

const toFilledPoint: (
  routePoint: SearchedSddClaimRoutePoint,
) => DestinationPoint = applySpec<DestinationPoint>({
  ...basePointSpec,
  externalID: getRoutePointExternalOrderId,
  _id: getRoutePointId,
})

export const toDestinationPoint = (
  routePoint?: SearchedSddClaimRoutePoint,
): DestinationPoint =>
  routePoint ? toFilledPoint(routePoint) : toInitialDestinationPoint(undefined)

const toInitialDestinationPoints: (
  routePoint?: SearchedSddClaimRoutePoint,
) => DestinationPoint[] = flow(toInitialDestinationPoint, castArray)

const mapIntoDestinationPoints: (
  routePoints?: SearchedSddClaimRoutePoints,
) => DestinationPoint[] = map(toDestinationPoint)

// --- Claim Item Helpers ---
const getDestinationPoint: (sddClaimProduct?: SddClaimProduct) => string = flow(
  get('droppof_point'),
  String,
)
const getProducts: (sddClaim?: SearchedSddClaim) => SddClaimProducts = flow(
  get('items'),
  defaultTo(stubArray()),
)
const groupItemsByDestinationPoint: (
  items?: SddClaimProducts,
) => Record<string, SddClaimProducts> =
  groupBy<SddClaimProduct>(getDestinationPoint)

const mapClaimItemsByDestinationPoint: (
  sddClaim?: SearchedSddClaim,
) => Record<string, SddClaimProducts> = flow(
  getProducts,
  groupItemsByDestinationPoint,
)

// --- Main Transformation Functions ---
export const toDestinationPoints = (
  sddClaim?: SearchedSddClaim,
  isCardClient = false,
  deliveryMethod?: DestinationPointDeliveryMethod,
): DestinationPoint[] => {
  const clientDeliveryMethod = isCardClient ? 'pickUpPoint' : 'address'

  if (sddClaim === undefined) {
    const destinationPoints = toInitialDestinationPoints(undefined)

    return destinationPoints.map(destinationPoint => {
      return merge(destinationPoint, {
        _deliveryMethod: deliveryMethod || clientDeliveryMethod,
      })
    })
  }

  const destinationPoints = pipe(
    sddClaim,
    getRoutePoints,
    filterDestinationPoints,
    sortByVisitOrder,
    mapIntoDestinationPoints,
  )
  const itemsByDestinationPoint = mapClaimItemsByDestinationPoint(sddClaim)

  return destinationPoints.map(destinationPoint => {
    const id = get('_id', destinationPoint)
    const paymentOnDelivery = get('paymentOnDelivery', destinationPoint)
    const items = get(id.toString(), itemsByDestinationPoint)

    return merge(destinationPoint, {
      parcels: toFilledParcels(items, paymentOnDelivery),
      _deliveryMethod: deliveryMethod || clientDeliveryMethod,
    })
  })
}
